/**
 * Lion Energy marks as PDF path operators, converted from the SVG sources in
 * `public/logos/` (`lion_energy_lion.svg` and `lion_energy_wordmark.svg`).
 *
 * The SVGs are pure straight-line polygons and cubic beziers, which map 1:1 onto
 * PDF's `m` / `l` / `c` / `h` operators. Coordinates are already flipped into
 * PDF's y-up space and expressed in the mark's own units — draw them inside a
 * `q ... cm ... Q` block that scales `width` x `height` to the size you want.
 *
 * Inlined rather than fetched at download time so certificate generation has no
 * network dependency and cannot half-render. Regenerate if the logos change.
 */

export type VectorMark = {
  /** Natural width of the mark, in its own coordinate units. */
  width: number;
  /** Natural height of the mark, in its own coordinate units. */
  height: number;
  /** Path construction operators; the caller supplies the fill colour and `f`. */
  path: string;
};

/** Standing lion silhouette (no wordmark). */
export const LION_MARK: VectorMark = {
  width: 65.42,
  height: 82.22,
  path:
  "52.41 54.1 m 53.94 56.97 l 46.87 58.69 l 52.41 54.1 l h 59.68 52.76 m 65.42 50.66 l 64.08 " +
  "43.96 l 65.04 39.94 l 63.32 40.9 l 52.03 40.52 l 60.64 43.2 l 62.74 48.17 l 59.68 52.76 l h " +
  "23.57 22.05 m 5.96 16.02 l 29.8 37.38 l 33.31 23.59 l 54 0 l 23.57 22.05 l h 58.64 32.85 m " +
  "62.74 32.1 l 63.32 36.12 l 60.26 33.82 l 46.1 41.47 l 39.98 37.27 l 36.92 40.52 l 43.61 " +
  "52.76 l 34.43 56.78 l 41.36 67.66 l 57.96 59.46 l 46.68 77.63 l 45.91 72.46 l 35.39 82.22 l " +
  "32.33 75.14 l 18.94 77.82 l 25.82 71.89 l 4.59 60.99 l 19.19 63.65 l 1.15 47.6 l 12.24 50.66 " +
  "l 0 33.06 l 8.99 39.94 l 3.25 20.24 l 30.8 45.11 l 39.08 25.39 l 56.24 4.36 l 55.28 16.03 l " +
  "61.4 12.4 l 55.09 23.49 l 54.71 33.63 l 58.64 32.88 l 58.64 32.85 l h ",
};

/** "LION ENERGY" wordmark, set as outlines. */
export const LION_WORDMARK: VectorMark = {
  width: 171,
  height: 18,
  path:
  "29.66 9.179 m 28.782 9.179 l 28.782 18 l 29.66 18 l 29.66 9.179 l h 28.8 0 m 23.57 0 19.684 " +
  "3.994 19.684 9.296 c 19.684 11.74 20.481 13.979 22.075 15.555 c 22.639 15.018 l 21.09 13.424 " +
  "20.472 11.463 20.472 9.322 c 20.472 4.504 24.081 0.878 28.791 0.878 c 33.502 0.878 37.111 " +
  "4.513 37.111 9.322 c 37.111 11.463 36.466 13.415 34.916 15.018 c 35.481 15.555 l 37.102 " +
  "13.988 37.899 11.74 37.899 9.296 c 37.899 3.994 34.012 0 28.782 0 c 28.8 0 l h 0 17.266 m " +
  "0.833 17.266 l 0.833 0.958 l 10.827 0.958 l 10.827 0.206 l 0 0.206 l 0 17.275 l 0 17.266 l h " +
  "15.51 17.266 m 16.343 17.266 l 16.343 0.197 l 15.51 0.197 l 15.51 17.266 l h 55.012 17.266 m " +
  "55.012 0.197 l 54.331 0.197 l 42.215 15.752 l 42.215 0.197 l 41.382 0.197 l 41.382 17.266 l " +
  "42.063 17.266 l 54.179 1.71 l 54.179 17.266 l 55.012 17.266 l h 74.293 1.764 m 74.293 0.206 " +
  "l 62.23 0.206 l 62.23 17.275 l 73.934 17.275 l 73.934 15.716 l 64.039 15.716 l 64.039 9.645 " +
  "l 72.86 9.645 l 72.86 8.104 l 64.039 8.104 l 64.039 1.764 l 74.302 1.764 l 74.293 1.764 l h " +
  "94.12 17.266 m 94.12 0.197 l 92.633 0.197 l 81.663 14.042 l 81.663 0.197 l 79.863 0.197 l " +
  "79.863 17.266 l 81.349 17.266 l 92.346 3.421 l 92.346 17.266 l 94.129 17.266 l 94.12 17.266 " +
  "l h 113.069 1.764 m 113.069 0.206 l 101.006 0.206 l 101.006 17.275 l 112.711 17.275 l " +
  "112.711 15.716 l 102.815 15.716 l 102.815 9.645 l 111.636 9.645 l 111.636 8.104 l 102.815 " +
  "8.104 l 102.815 1.764 l 113.078 1.764 l 113.069 1.764 l h 130.281 0.206 m 126.376 5.686 l " +
  "125.937 5.642 125.499 5.615 125.015 5.615 c 120.43 5.615 l 120.43 0.206 l 118.63 0.206 l " +
  "118.63 17.275 l 125.015 17.275 l 129.358 17.275 131.991 15.081 131.991 11.427 c 131.991 8.74 " +
  "130.576 6.869 128.087 6.063 c 132.26 0.215 l 130.281 0.215 l 130.281 0.206 l h 130.182 " +
  "11.418 m 130.182 14.149 128.382 15.707 124.961 15.707 c 120.43 15.707 l 120.43 7.146 l " +
  "124.961 7.146 l 128.373 7.146 130.182 8.731 130.182 11.409 c 130.182 11.418 l h 149.588 " +
  "8.731 m 151.317 8.731 l 151.317 2.221 l 149.705 0.779 147.412 0.054 145.048 0.054 c 139.854 " +
  "0.054 136.057 3.734 136.057 8.731 c 136.057 13.728 139.863 17.409 145.075 17.409 c 147.663 " +
  "17.409 149.902 16.576 151.415 14.946 c 150.296 13.8 l 148.854 15.215 147.152 15.797 145.155 " +
  "15.797 c 140.937 15.797 137.839 12.797 137.839 8.731 c 137.839 4.666 140.937 1.666 145.128 " +
  "1.666 c 146.758 1.666 148.272 2.033 149.588 2.982 c 149.588 8.731 l h 163.594 6.107 m " +
  "163.594 0.206 l 161.812 0.206 l 161.812 6.107 l 154.988 17.275 l 156.914 17.275 l 162.761 " +
  "7.692 l 168.609 17.275 l 170.409 17.275 l 163.585 6.107 l 163.594 6.107 l h ",
};
